import { isEmpty } from "@swiggy-private/core-ui";

interface Color {
    gradient?: {
        offset: string;
        stopColor: string;
        key: number;
    };
    r: number;
    g: number;
    b: number;
    opacity: number;
}

const getRGBStyle = ({ r, g, b, opacity }: Color): string => `rgba(${r}, ${g}, ${b}, ${opacity})`;

export const getColorsRGB = (colors: { colorCode: string; duration: number }[]): [Color, number][] =>
    colors.map(color => {
        const rgb = color.colorCode
            .replace(/^#?([a-f\d])([a-f\d])([a-f\d])$/i, (_m, r, g, b) => `#${r}${r}${g}${g}${b}${b}`)
            .substring(1)
            .match(/.{2}/g)
            ?.map(x => parseInt(x, 16));

        if (!rgb) {
            throw new Error("Error in genrating color code");
        }
        return [{ r: rgb[0], g: rgb[1], b: rgb[2], opacity: 1 }, color.duration];
    });

const getColorsBase = (colors: { colorCode: string; duration: number }[]): [Color, number][] => {
    const colorsRGB = getColorsRGB(colors);
    // add gradient props
    let colorsTotalDurationPct = 0;
    let colorBase: [Color, number][] = [];
    colorBase = colorsRGB.map((color, index) => {
        const isLastColor = colorsRGB.length === index + 1;
        let offset = "0%";

        if (index > 0) {
            const prevColorDuration = colorsRGB[index - 1][1];
            const offsetPct = Math.round(prevColorDuration * 100);
            colorsTotalDurationPct += offsetPct;

            if (colorsTotalDurationPct > 100 || isLastColor) {
                colorsTotalDurationPct = 100;
                offset = "100%";
            } else {
                offset = `${colorsTotalDurationPct}%`;
            }
        }

        const gradient = { offset, stopColor: getRGBStyle(color[0]), key: index };
        return [{ ...color[0], gradient }, color[1]];
    });

    return colorBase;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getNormalizedColors = (colors: { colorCode: string; duration: number }[], duration: number): any[] => {
    const colorsBase = getColorsBase(colors);
    let colorsTotalDuration = 0;

    return colorsBase.map((color, index: number) => {
        const isLastColor = colorsBase.length === index + 1;
        if (colorsTotalDuration >= duration || isLastColor) {
            colorsTotalDuration = duration;
            return { ...color[0], colorEndTime: colorsTotalDuration };
        }

        const colorStartTime = colorsTotalDuration;
        const colorEndTimeTemp = color[1] !== undefined ? colorStartTime + color[1] * duration : duration;
        const colorEndTime = colorEndTimeTemp >= duration ? duration : colorEndTimeTemp;

        const nextColor = colorsBase[index + 1][0];
        const goals = {
            goalR: nextColor.r - color[0].r,
            goalG: nextColor.g - color[0].g,
            goalB: nextColor.b - color[0].b,
        };

        colorsTotalDuration = colorEndTime;

        return {
            ...color[0],
            ...goals,
            colorStartTime,
            colorEndTime,
            duration: colorEndTime - colorStartTime,
        };
    });
};

export const linearEase = (time: number, start: number, goal: number, duration: number): number => {
    if (duration === 0) {
        return goal;
    }

    const currentTime = time / duration;
    return start + goal * currentTime;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getStroke = (normalizedColors: any[], elapsedTime: number): string => {
    if (normalizedColors.length === 1) {
        return getRGBStyle(normalizedColors[0]);
    }

    const color = normalizedColors.find(({ colorEndTime }: { colorEndTime: number }) => elapsedTime <= colorEndTime);
    if (color.duration === undefined) {
        return getRGBStyle(color);
    }
    const currentColorStartTime = elapsedTime - color.colorStartTime;
    const r = linearEase(currentColorStartTime, color.r, color.goalR, color.duration) | 0;
    const g = linearEase(currentColorStartTime, color.g, color.goalG, color.duration) | 0;
    const b = linearEase(currentColorStartTime, color.b, color.goalB, color.duration) | 0;

    return getRGBStyle({ r, g, b, opacity: color.opacity });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getStrokeV4 = (normalizedColors: any[], elapsedTime: number): string => {
    if (normalizedColors.length === 1) {
        return getRGBStyle(normalizedColors[0]);
    }

    const color = normalizedColors.find(({ colorEndTime }: { colorEndTime: number }) => elapsedTime <= colorEndTime);
    // When elapsedTime is more than the colorEndTime we get an undefined color which breaks flows.
    // This happens for webview refresh cases, so fallback to the last color
    return !isEmpty(color) ? getRGBStyle(color) : "#E53554";
};
